import clsx from "clsx";
import { useMemo } from "react";
import { api } from "../../api";
import LoadingText from "../LoadingText";
import { Badge } from "../tailwind/badge";
import { Divider } from "../tailwind/divider";
import { Text } from "../tailwind/text";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { useFindMany } from "@gadgetinc/react";
import { calculateRate } from "../../functions/calculateRate";
import { PencilIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon, PlayCircleIcon } from "@heroicons/react/16/solid";
import { Button } from "../tailwind/button";
import { useNavigate } from "react-router";
import { Link } from "../tailwind/link";
import { Icon, Tooltip } from "@shopify/polaris";
import { mappingAutomationTypeWithName } from "../../externalPages/automations/shared/mapping-automation-type-with-name.const";
import { getAutomationTypeIcon } from "../../externalPages/automations/helpers/functions";
import { formatDateTime } from "../../functions/date-utils";

export function ViewActiveAutomations({
  customerId,
  className,
  isMobile,
  integrations,
}) {
  const navigate = useNavigate();
  const [{ data: lastAutomations, fetching }] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customerId },
      isArchived: { notEquals: true },
      trigger: { notEquals: "manual" },
      isAutomationActive: { equals: true },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
      automationType: true,
      scheduledDate: true,
      attributedRevenueShopify: true,
      updatedAt: true,
      campaignStats: {
        delivered30days: true,
        sent30days: true,
        read30days: true,
        turnover30days: true,
      },
    },
  });

  const lastAutomationsSent = useMemo(() => {
    if (!lastAutomations || lastAutomations.length === 0) return [];

    return [...lastAutomations]
      .sort((a, b) => {
        const aHasScheduled = !!a.scheduledDate;
        const bHasScheduled = !!b.scheduledDate;

        if (aHasScheduled && !bHasScheduled) return -1;
        if (!aHasScheduled && bHasScheduled) return 1;

        const aDate = new Date(a.scheduledDate || a.updatedAt);
        const bDate = new Date(b.scheduledDate || b.updatedAt);

        return bDate - aDate;
      })
      .slice(0, 5);
  }, [lastAutomations]);

  return (
    <div
      className={clsx(className, "border rounded-xl p-3 shadow bg-white mb-6")}
    >
      <div className="flex justify-between items-center mb-2">
        <Link onClick={() => navigate("/automations")}>
          <div className="flex items-center hover:underline hover:cursor-pointer">
            <Text className="text-lg font-bold">Active automations</Text>
            <ChevronRightIcon className="mt-1 w-6" />
          </div>
        </Link>
        <Text className="text-sm ">Last 30 days</Text>
      </div>
      <Divider />

      <Table dense>
        <TableHead>
          <TableRow>
            {!isMobile && <TableHeader>Status</TableHeader>}
            <TableHeader>Automation</TableHeader>
            {!isMobile && (
              <TableHeader className="text-center">Type</TableHeader>
            )}
            <TableHeader>Last sending</TableHeader>
            <TableHeader className="text-center">Delivered</TableHeader>
            {!isMobile && (
              <TableHeader className="text-center">Open rate</TableHeader>
            )}
            {integrations.shopify && !isMobile && (
              <>
                <TableHeader className="text-center">Turnover</TableHeader>
                <TableHeader className="text-center">ARPC</TableHeader>
              </>
            )}
            {!isMobile && <TableHeader className="text-center"></TableHeader>}
          </TableRow>
        </TableHead>
        <TableBody>
          {fetching ? (
            <TableRow>
              <TableCell colSpan={7}>
                <LoadingText className="my-3 h-[24px]" />
              </TableCell>
            </TableRow>
          ) : lastAutomationsSent.length > 0 ? (
            <>
              {lastAutomationsSent.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  integrations={integrations}
                  isMobile={isMobile}
                  navigate={navigate}
                />
              ))}
              {lastAutomations.length > 5 && (
                <TableRow>
                  <TableCell className="text-gray-500">
                    <Link
                      onClick={() => navigate("/automations")}
                      className="hover:cursor-pointer"
                    >
                      ...
                    </Link>
                  </TableCell>
                </TableRow>
              )}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={8}>
                <Text className="text-center py-4 text-lg text-gray-700">
                  Start creating automations 📦
                </Text>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CampaignRow({ campaign, integrations, isMobile, navigate }) {
  const automationTypeIcon = getAutomationTypeIcon(campaign.automationType);
  const campaignStats = campaign.campaignStats;

  const {
    turnover30days = 0,
    sent30days = 0,
    read30days = 0,
    delivered30days = 0,
  } = campaignStats || {};

  const openRate =
    read30days > 0 && sent30days > 0
      ? calculateRate(read30days, delivered30days).toFixed(0)
      : null;

  return (
    <TableRow
      key={campaign.id}
      href={`/analytics/${campaign.id}`}
      className="group hover:bg-muted transition"
    >
      {!isMobile && (
        <TableCell>
          <Badge color="lime">
            <PlayCircleIcon className="h-5 w-5" />
            <span>Live</span>
          </Badge>
        </TableCell>
      )}
      <TableCell>{campaign.name}</TableCell>

      {!isMobile && (
        <TableCell className="text-center">
          <Tooltip
            content={
              mappingAutomationTypeWithName[campaign.automationType] ?? "API"
            }
          >
            <Icon
              source={automationTypeIcon}
              color="base"
              accessibilityLabel="Automatisation"
            />
          </Tooltip>
        </TableCell>
      )}

      <TableCell>
        {campaign.scheduledDate
          ? isMobile
            ? formatDateTime(new Date(campaign.scheduledDate), "shortDate")
            : formatDateTime(
                new Date(campaign.scheduledDate),
                "shortTimeMediumDate",
              )
          : "-"}
      </TableCell>

      <TableCell className="text-center">{delivered30days ?? "-"}</TableCell>
      {!isMobile && (
        <TableCell className="text-center">
          {openRate ? `${openRate}%` : "-"}
        </TableCell>
      )}
      {integrations.shopify && !isMobile && (
        <>
          <TableCell className="text-center">
            {turnover30days ? `${turnover30days.toFixed(0)}€` : "-"}
          </TableCell>
          <TableCell className="text-center">
            {sent30days && turnover30days
              ? (turnover30days / Math.max(1, sent30days)).toFixed(2) + "€"
              : "-"}
          </TableCell>
        </>
      )}
      {!isMobile && (
        <>
          <TableCell className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center">
            <Button
              plain
              aria-label="Edit automation"
              className="h-[25px]"
              onClick={() => {
                navigate(`/automations/edit/${campaign.id}`);
              }}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
